/**
 * エラー行の見せ方（docs/01 6-4「何行目の、どの項目が、なぜ駄目か」）。
 *
 * サーバは「行番号・項目のキー・理由」を返します。画面ではそれに加えて、
 * **どう直せばよいか**を1行で添えます（docs/06 原則2「理由だけで終わらせない」）。
 */
import type { RoleRow, RosterColumn, RosterRowIssue } from '../api/types.js';
import { ROLE_REQUIREMENTS, type RoleWording } from '../auth/role-wording.js';

/**
 * 直し方に役職の呼び名を出すための材料（その会社の呼び名・issue #480）。
 * `roles` はログインのときに読んだ役職の一覧（`GET /org/roles`）。
 */
export interface RosterFixContext {
  wording: RoleWording;
  roles: readonly RoleRow[];
}

/**
 * CSV の役職の欄に書ける呼び名の案内（以前のサーバの言い方のときだけ使う。いまのサーバは
 * 理由の文に書き方まで入れて返す）。その会社のいまの呼び名を「一般 → 拠点 → 本部」の順で並べる。
 */
function roleSpellingHint(roles: readonly RoleRow[]): string {
  const listed = [...roles].sort(
    (a, b) => b.sort_order - a.sort_order || a.key.localeCompare(b.key),
  );
  const names = listed.map((role) => `「${role.display_name}」`).join('');
  const fallback = roles.find((role) => role.key === 'member')?.display_name ?? '一般';
  return `役職は${names}のどれかで書いてください（空なら「${fallback}」になります）。`;
}

/**
 * 画面に出すときの見出しの言い換え（総点検 J-05・J-86）。
 *
 * 「役職キー」の「キー」は開発の言葉、「基幹スタッフID」はメンバーの画面の「スタッフID」と
 * 呼び方が違うので、画面ではそろえた名前で出します。どちらの名前で書いたCSVも、
 * サーバは同じ項目として受け取ります（見出しの言い換えの表・server/src/roster/columns.ts）。
 */
const DISPLAY_COLUMN_LABELS: Record<string, string> = {
  役職キー: '役職',
  基幹スタッフID: 'スタッフID',
};

export function displayColumnLabel(label: string): string {
  return DISPLAY_COLUMN_LABELS[label] ?? label;
}

/** 項目のキー → 日本語の名前（サーバが返す項目の決まりから引く）。 */
export function fieldLabel(field: string | undefined, columns: readonly RosterColumn[]): string {
  if (field === undefined) return '行全体';
  const label = columns.find((column) => column.key === field)?.label;
  return label === undefined ? '（項目）' : displayColumnLabel(label);
}

/**
 * サーバの理由の文を「なぜ」と「どう直すか」に分ける。
 *
 * サーバは理由のあとに直し方まで書いて返します（例:「役職の書き方が違います。役職は…で書いてください」）。
 * 1文目を「なぜ」、2文目から先を「どう直すか」に出し、同じことを2回並べないようにします。
 * 直し方が書かれていない理由のときは、`fix` は null です。
 */
export function splitReason(reason: string): { why: string; fix: string | null } {
  const end = reason.indexOf('。');
  if (end < 0 || end === reason.length - 1) return { why: reason, fix: null };
  const rest = reason.slice(end + 1).trim();
  return rest.includes('ください')
    ? { why: reason.slice(0, end + 1), fix: rest }
    : { why: reason, fix: null };
}

/**
 * 「なぜ」の欄に出す言葉。
 * 以前のサーバは役職の書き方の誤りで内部の値（英字）を並べていたので、画面では言い換える（総点検 J-05）。
 */
export function reasonLabel(issue: RosterRowIssue): string {
  if (isInvalidRole(issue)) return '役職の書き方が違います。';
  return splitReason(issue.reason).why;
}

/** 役職の欄に、受け付けない値が書いてある行か（以前のサーバの言い方）。 */
function isInvalidRole(issue: RosterRowIssue): boolean {
  return issue.field === 'roleKey' && /役職キーが正しくありません/.test(issue.reason);
}

/**
 * どう直すか。理由の文にそろえて、その場でできることを書く。
 * 当てはまるものが無ければ、共通の案内を出す（何も出さない、にはしない）。
 */
export function howToFix(
  issue: RosterRowIssue,
  columns: readonly RosterColumn[],
  context: RosterFixContext,
): string {
  const reason = issue.reason;
  // サーバが直し方まで書いているときは、それをそのまま出す
  const fix = splitReason(reason).fix;
  if (fix !== null) return fix;
  if (reason.includes('同じCSV')) {
    // 名簿にいる方と重なったのではなく、同じファイルの中の行どうしが重なった（#343 L-4）
    return '同じCSVの中に、同じ値の行があります。どちらが正しいかを確かめて、片方を直してください。';
  }
  if (reason.includes('重複') || reason.includes('重なっています')) {
    return '同じ値の方がすでにいます。どちらが正しいかを確かめて、片方を直してください。';
  }
  if (reason.includes('すでに別の方に登録されています')) {
    return 'ほかの方と同じ値になっています。CSVの値をご確認ください。';
  }
  if (isInvalidRole(issue)) {
    // 新しく登録する方の行で出るので、「変えられません」とは言わない（総点検 J-05）
    return roleSpellingHint(context.roles);
  }
  if (reason.includes('メンバーを管理できない') || reason.includes('名簿を管理できない')) {
    return `この行は、${context.wording.who(ROLE_REQUIREMENTS.rosterImport)}に取り込みをご依頼ください。`;
  }
  if (reason.includes('ご自分の拠点以外') || reason.includes('自分の拠点以外')) {
    return `ご自分の拠点の方の行だけにしてください。ほかの拠点の方は、${context.wording.who(
      ROLE_REQUIREMENTS.rosterImportOrgWide,
    )}に取り込みをご依頼ください。`;
  }
  if (issue.field === 'roleKey' && reason.includes('役職')) {
    return (
      '役職はCSVでは変えられません。この列を空にするか、いまと同じ役職にしてください' +
      '（役職の変更はメンバーの画面から1人ずつ行います）。'
    );
  }
  if (reason.includes('拠点')) {
    return '拠点名を、拠点の一覧にある名前と同じにしてください。';
  }
  if (reason.includes('無効')) {
    return 'メンバーの画面でこの方の無効を解除してから、もう一度取り込んでください。';
  }
  if (issue.field !== undefined) {
    return `CSVの「${fieldLabel(issue.field, columns)}」の欄を直して、もう一度取り込んでください。`;
  }
  return 'この行を直して、もう一度取り込んでください。';
}
